/**
 * @fileoverview ConditionalPageLoader for loading modules based on page-specific conditions.
 * 
 * This loader handles modules that should only be loaded under specific page conditions,
 * such as particular page types, URL patterns, content presence, or other page-specific
 * requirements and business rules.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { IConditionalLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";
import { initOnceWithDOMReady } from "../../util/dom-loader-check";

/**
 * ConditionalPageLoader manages page-specific conditional module loading.
 * 
 * This loader is responsible for:
 * - Evaluating page-specific conditions and requirements
 * - Loading modules based on page type, template, or content
 * - Handling URL pattern-based module loading
 * - Managing page-specific business rule implementations
 * - Supporting conditional functionality based on page context
 * 
 * The ConditionalPageLoader follows the singleton pattern and implements the IConditionalLoader
 * interface for consistent behavior across the router system.
 * 
 * Potential Page Conditions:
 * - Page template type (contact, product, checkout, etc.)
 * - URL patterns and routes
 * - Presence of specific DOM elements or content
 * - Page metadata and configuration
 * - User authentication state on specific pages
 * - Geographic or localization requirements
 * - A/B testing page variants
 * - Business rule-based page enhancements
 * 
 * Example Use Cases:
 * - Load contact form validation on contact pages
 * - Add e-commerce modules on product/checkout pages
 * - Load analytics modules on specific landing pages
 * - Add accessibility modules on designated pages
 * - Load region-specific modules based on page content
 * 
 * Features:
 * - Singleton pattern for global access
 * - Page condition evaluation and detection
 * - Secure module loading via RouterHelper
 * - Extensible architecture for new page conditions
 * - Comprehensive error handling and logging
 * 
 * @class ConditionalPageLoader
 * @implements {IConditionalLoader}
 */
export class ConditionalPageLoader implements IConditionalLoader {
    /** Singleton instance of the ConditionalPageLoader */
    private static instance: ConditionalPageLoader;
    
    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();
    
    /** Current status and state of the ConditionalPageLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };
    
    /**
     * Gets the singleton instance of ConditionalPageLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton ConditionalPageLoader instance
     */
    static getInstance(): ConditionalPageLoader {
        this.instance ??= new ConditionalPageLoader();
        return this.instance;
    }    
    
    /**
     * Initializes the ConditionalPageLoader.
     * 
     * Sets up the loader for page-specific conditional module loading.
     * This is a lightweight initialization as page condition evaluation
     * happens during the actual loading phase.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When initialization fails
     */
    async init(): Promise<void> {
        try {
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'ConditionalPageLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Loads all conditional page modules (delegates to loadConditionalPageModules).
     * 
     * This is the main entry point for loading conditional page modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all conditional page modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.loadConditionalPageModules();
    }

    /**
     * Placeholder for feature-specific conditional loading.
     * 
     * ConditionalPageLoader focuses on page-specific conditions rather than
     * browser feature capabilities, so this method logs that no feature
     * modules are handled. Required by the IConditionalLoader interface.
     * 
     * @returns Promise that resolves immediately
     */
    async loadConditionalFeatureModules(): Promise<void> {
        // ConditionalPageLoader doesn't handle feature modules, but interface requires it
        this.routerHelper.routerConsoleLog('ConditionalPageLoader does not handle feature modules', false, 'ConditionalPageLoader');
    }

    /**
     * Loads modules based on page-specific conditions and requirements.
     * 
     * This method evaluates various page conditions and loads appropriate modules
     * based on the current page context, content, and business rules.
     * 
     * Page Condition Evaluation Strategy:
     * - Analyze current page template and type
     * - Check URL patterns and routing information
     * - Scan DOM for specific content or elements
     * - Evaluate page metadata and configuration
     * - Apply business rules and conditional logic
     * 
     * Example Implementation Ideas:
     * ```typescript
     * // Load contact form modules on contact pages
     * if (window.location.pathname.includes('/contact') || 
     *     document.querySelector('form[data-contact]')) {
     *   await this.routerHelper.loadModuleSafely(
     *     'contact-form',
     *     () => import('./contact-form-module'),
     *     'Contact Form'
     *   );
     * }
     * 
     * // Load e-commerce modules on product pages
     * if (document.querySelector('[data-product-id]')) {
     *   await this.routerHelper.loadModuleSafely(
     *     'product-enhancement',
     *     () => import('./product-module'),
     *     'Product Enhancement'
     *   );
     * }
     * 
     * // Load analytics on landing pages
     * if (document.querySelector('[data-landing-page]')) {
     *   await this.routerHelper.loadModuleSafely(
     *     'landing-analytics',
     *     () => import('./analytics-module'),
     *     'Landing Analytics'
     *   );
     * }
     * ```
     * 
     * Currently includes commented example for contact form loading that
     * can be uncommented and expanded based on specific requirements.
     * 
     * @returns Promise that resolves when conditional page modules are loaded
     * @throws {Error} When page condition evaluation or module loading fails
     */
    async loadConditionalPageModules(): Promise<void> {
        this.status.isLoading = true;
        
        try {

            // Load Gift Certificate Image module on gift certificate image pages
            const currentUrl = globalThis.location.href;
            const isGiftCertificationImg = currentUrl.includes("gift-certificate-image");
            if (isGiftCertificationImg) {
                await this.routerHelper.loadModuleSafely(
                    'gift-certificate-image',
                    async () => {
                        const mod = await import(
                            /* webpackChunkName: "gift-certificate-image-loader" */ '../../mly/gift-certification-image'
                        );
                        
                        // GiftCertificateImage has DOMContentLoaded listener with URL condition check
                        
                        if (mod.GiftCertificateImage) {
                                // DOMContentLoaded won't fire on bfcache restore. The singleton attaches the click listener only once, while reinitialize() refreshes query params and the image without duplicating listeners.
                                initOnceWithDOMReady('GiftCertificateImage', (isPersistedReset) => {
                                    const instance = mod.GiftCertificateImage.getInstance();
                                    if (isPersistedReset) {
                                        instance.reinitialize();
                                    }
                                }, { resetOnPersistedNavigation: true });
                        }
                        
                        return mod;
                    },
                    'GiftCertificateImage'
                );
                this.status.loadedModules.push('gift-certificate-image');
            }
            this.routerHelper.routerConsoleLog('Conditional page modules loading completed', false, 'ConditionalPageLoader');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.status.failedModules.push('conditional-page-modules');
            this.status.lastError = errorMessage;
            this.routerHelper.routerConsoleLog(`Failed to load conditional page modules - ${errorMessage}`, true, 'ConditionalPageLoader');
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}
